import { Component, effect, inject, input, output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NotificationSetting } from '../../../../../viewmodels/apple/profiles/notifications-profile/notification-setting.model';

@Component({
  selector: 'ml-mdm-notification-setting',
  templateUrl: './notification-setting.component.html',
  styleUrls: ['./notification-setting.component.scss'],
  imports: [ReactiveFormsModule, FaIconComponent],
})
export class NotificationSettingComponent {
  private readonly formBuilder = inject(UntypedFormBuilder);

  readonly title = input('Notification Settings');
  readonly notificationSetting = input.required<NotificationSetting>();
  readonly notificationSettingChange = output<NotificationSetting>();

  readonly mutate = input(false);
  readonly allowToggle = input(true);

  editing = true;

  public notificationSettingForm: UntypedFormGroup;

  constructor() {
    this.createForm();

    effect(() => {
      const notificationSetting = this.notificationSetting();
      this.rebuildForm(notificationSetting);
    });
  }

  doneEditing(): boolean {
    if (!this.editing) {
      return true;
    }

    if (this.notificationSettingForm.status !== 'VALID') {
      return false;
    }

    const result = this.populate();
    this.notificationSettingChange.emit(result);
    return true;
  }

  toggleEditing() {
    // Toggle if we are not editing, or if the edited form validates
    if (!this.editing || this.doneEditing()) {
      this.editing = !this.editing;
    }
  }

  private createForm() {
    this.notificationSettingForm = this.formBuilder.group({
      bundleIdentifier: ['', Validators.required],
      notificationsEnabled: false,
      showInNotificationCenter: false,
      showInLockScreen: false,
      alertType: 0,
      badgesEnabled: false,
      soundsEnabled: false,
      showInCarPlay: false,
      criticalAlertEnabled: false,
    });
  }

  private rebuildForm(notificationSetting: NotificationSetting) {
    this.notificationSettingForm.reset({
      bundleIdentifier: notificationSetting.bundleIdentifier,
      notificationsEnabled: notificationSetting.notificationsEnabled,
      showInNotificationCenter: notificationSetting.showInNotificationCenter,
      showInLockScreen: notificationSetting.showInLockScreen,
      alertType: notificationSetting.alertType,
      badgesEnabled: notificationSetting.badgesEnabled,
      soundsEnabled: notificationSetting.soundsEnabled,
      showInCarPlay: notificationSetting.showInCarPlay,
      criticalAlertEnabled: notificationSetting.criticalAlertEnabled,
    });
  }

  private populate(): NotificationSetting {
    let notificationSetting = this.notificationSetting();
    if (!this.mutate()) {
      notificationSetting = new NotificationSetting();
    }

    notificationSetting.bundleIdentifier = this.notificationSettingForm.get('bundleIdentifier')?.value;
    notificationSetting.notificationsEnabled = this.notificationSettingForm.get('notificationsEnabled')?.value;
    notificationSetting.showInNotificationCenter = this.notificationSettingForm.get('showInNotificationCenter')?.value;
    notificationSetting.showInLockScreen = this.notificationSettingForm.get('showInLockScreen')?.value;
    notificationSetting.alertType = +this.notificationSettingForm.get('alertType')?.value;
    notificationSetting.badgesEnabled = this.notificationSettingForm.get('badgesEnabled')?.value;
    notificationSetting.soundsEnabled = this.notificationSettingForm.get('soundsEnabled')?.value;
    notificationSetting.showInCarPlay = this.notificationSettingForm.get('showInCarPlay')?.value;
    notificationSetting.criticalAlertEnabled = this.notificationSettingForm.get('criticalAlertEnabled')?.value;

    return notificationSetting;
  }
}
